import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '../api.service';
import { localStorageService } from '../localstorage.service';
import { CAPS, Capability, DashboardAccess } from './capabilities.constants';
import { buildSalesPermissionFlags, SalesPermissionFlags } from './sales-permission-flags';

/**
 * Storage keys we own. These live in the same `finance_mutual_admin_storage`
 * blob that `localStorageService` uses for everything else (see
 * `customGetItem` / `customSetItem`).
 *
 * - `capabilities`   : string[]  — flat list as returned by the backend, or `['*']` for wildcard / super-admin
 * - `dashboards`     : { l1, l2, l3 } — backend-derived flags for sales dashboards
 */
const STORAGE_KEYS = Object.freeze({
  CAPABILITIES: 'capabilities',
  DASHBOARDS: 'dashboards'
});

const WILDCARD = '*';

/**
 * Snapshot of everything the FE needs to render permission-aware UI.
 * Emitted as a single object so subscribers don't have to combine three streams.
 */
export interface PermissionsSnapshot {
  /** True when the user is super-admin / has `['*']`. */
  wildcard: boolean;
  /** Set of `'path:code'` strings the user has been granted. */
  set: Set<Capability>;
  /** Sales dashboard visibility booleans. */
  dashboards: DashboardAccess;
}

/**
 * Single source of truth for capability-driven UI authz on the frontend.
 *
 * Lifecycle:
 *  1. On login, `LoginComponent` calls `setFromLoginResponse(response)` with the
 *     `capabilities` and `dashboards` fields embedded by `auth/email_login_v2`.
 *  2. On every page load this service hydrates from `localStorage`
 *     synchronously (constructor) so guards and `*ngIf` checks have data
 *     before the first change-detection pass.
 *  3. On focus / app init, `MainLayoutComponent` should call
 *     `refresh()` to reconcile against `/admin/auth/me/capabilities`. This
 *     keeps the FE in sync when an admin's grants change between sessions.
 *
 * The service is `providedIn: 'root'` so any component, guard, directive or
 * other service can inject it without module wiring.
 */
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private readonly _local = inject(localStorageService);
  private readonly _api = inject(ApiService);

  private readonly _wildcard$ = new BehaviorSubject<boolean>(false);
  private readonly _set$ = new BehaviorSubject<Set<Capability>>(new Set());
  private readonly _dashboards$ = new BehaviorSubject<DashboardAccess>({ l1: false, l2: false, l3: false, l4: false });

  /** Live capability set (read-only stream). */
  readonly capabilities$: Observable<Set<Capability>> = this._set$.asObservable();
  readonly wildcard$: Observable<boolean> = this._wildcard$.asObservable();
  readonly dashboards$: Observable<DashboardAccess> = this._dashboards$.asObservable();

  /** Snapshot stream — most components want this. */
  readonly snapshot$: Observable<PermissionsSnapshot> = combineLatest([
    this._wildcard$,
    this._set$,
    this._dashboards$
  ]).pipe(
    map(([wildcard, set, dashboards]) => ({ wildcard, set, dashboards }))
  );

  // -------- Convenience streams used by sales components --------------------

  /**
   * `agent` semantically = "view_own only" (no team or manager scope). Used by
   * single-dashboard FE pages to know if they should hide team controls.
   */
  readonly isAgent$: Observable<boolean> = this.snapshot$.pipe(
    map(s =>
      !s.wildcard &&
      (s.set.has(CAPS.SALES.LEADS.VIEW_OWN) || s.set.has(CAPS.SALES.BUSINESS_VALIDATION.VIEW_OWN)) &&
      !s.set.has(CAPS.SALES.LEADS.VIEW_TEAM) && !s.set.has(CAPS.SALES.BUSINESS_VALIDATION.VIEW_TEAM) &&
      !s.set.has(CAPS.SALES.LEADS.VIEW_ALL)
    )
  );

  /** Team Lead = view_team without view_all. Wildcard does NOT count as TL. */
  readonly isTeamLead$: Observable<boolean> = this.snapshot$.pipe(
    map(s => !s.wildcard && (s.set.has(CAPS.SALES.LEADS.VIEW_TEAM) || s.set.has(CAPS.SALES.BUSINESS_VALIDATION.VIEW_TEAM)) && !s.set.has(CAPS.SALES.LEADS.VIEW_ALL))
  );

  /** Manager = view_all OR wildcard / super-admin. */
  readonly isManager$: Observable<boolean> = this.snapshot$.pipe(
    map(s => s.wildcard || s.set.has(CAPS.SALES.LEADS.VIEW_ALL))
  );

  /** Training: caller is allowed to manage another admin's training slots. */
  readonly canManageOtherTrainingSlots$: Observable<boolean> = this.snapshot$.pipe(
    map(s => this.evaluate(s, CAPS.TRAINING.SLOTS.MANAGE_OTHERS))
  );

  /** Training: caller's demos route to the existing L1 assignee, not themselves. */
  readonly canBookForTeam$: Observable<boolean> = this.snapshot$.pipe(
    map(s => this.evaluate(s, CAPS.TRAINING.BOOKING.BOOK_FOR_TEAM))
  );

  /** Training: caller is on L1 tier (reschedule semantics). */
  readonly isL1Tier$: Observable<boolean> = this.snapshot$.pipe(
    map(s => this.evaluate(s, CAPS.TRAINING.BOOKING.L1_TIER))
  );

  /** RBAC mutations allowed. */
  readonly canManageRbac$: Observable<boolean> = this.snapshot$.pipe(
    map(s => this.evaluate(s, CAPS.RBAC.MANAGE))
  );

  constructor() {
    this.hydrateFromLocalStorage();
  }

  // ===== Public mutations ===================================================

  /**
   * Hydrate the service from a successful login response. The backend embeds
   * `capabilities` (string[] or ['*']) and `dashboards` (l1/l2/l3 booleans)
   * in `auth/email_login_v2`'s response — see `backend/admin/controllers/auth.controller.js`.
   */
  setFromLoginResponse(response: any): void {
    const list: string[] = Array.isArray(response?.capabilities) ? response.capabilities : [];
    const dashboards: DashboardAccess = response?.dashboards
      ? {
          l1: !!response.dashboards.l1,
          l2: !!response.dashboards.l2,
          l3: !!response.dashboards.l3,
          l4: !!response.dashboards.l4
        }
      : { l1: false, l2: false, l3: false, l4: false };
    this.applyCapabilities(list, dashboards);
  }

  /**
   * Refresh capabilities from `/admin/auth/me/capabilities`. Call on app
   * init / window focus / token refresh. Returns an observable that emits
   * once the snapshot has been updated. Errors are swallowed (we keep using
   * the cached snapshot) but logged via the console.
   */
  refresh(): Observable<PermissionsSnapshot> {
    return this._api.api({}, 'auth/me/capabilities', 'get').pipe(
      map((res: any) => {
        if (!res || res.is_error) {
          return this.currentSnapshot();
        }
        // /me/capabilities wraps the payload in `data` (per rbacController.getMyCapabilities).
        // Fall back to top-level for forward-compat if the envelope is ever flattened.
        const payload = res.data ?? res;
        const list: string[] = payload.wildcard
          ? [WILDCARD]
          : Array.isArray(payload.capabilities) ? payload.capabilities : [];
        // /me/capabilities does not currently include `dashboards`; derive it
        // from the cap set so single source of truth stays the cap registry.
        const dashboards: DashboardAccess = {
          l1: list.includes(WILDCARD) || list.includes(CAPS.SALES.DASHBOARD_L1.VIEW),
          l2: list.includes(WILDCARD) || list.includes(CAPS.SALES.DASHBOARD_L2.VIEW),
          l3: list.includes(WILDCARD) || list.includes(CAPS.SALES.DASHBOARD_L3.VIEW),
          l4: list.includes(WILDCARD) || list.includes(CAPS.SALES.DASHBOARD_BUSINESS_VALIDATION.VIEW)
        };
        this.applyCapabilities(list, dashboards);
        return this.currentSnapshot();
      }),
      catchError(err => {
        console.warn('[PermissionsService.refresh] failed; keeping cached snapshot', err);
        return of(this.currentSnapshot());
      })
    );
  }

  /** Clear cached state on logout. Called by `localStorageService.customClearAllItems()` flow. */
  clear(): void {
    this._wildcard$.next(false);
    this._set$.next(new Set());
    this._dashboards$.next({ l1: false, l2: false, l3: false , l4: false });
  }

  // ===== Sync queries =======================================================

  /**
   * Synchronous capability check. Returns true when the user has the wildcard
   * grant or has the exact `path:code` string. Use this in components,
   * directives and guards where you don't want to subscribe.
   */
  hasCap(cap: Capability): boolean {
    if (this._wildcard$.value) { return true; }
    return this._set$.value.has(cap);
  }

  /** True if ANY of the given caps is granted. */
  hasAnyCap(caps: Capability[]): boolean {
    if (this._wildcard$.value) { return true; }
    const set = this._set$.value;
    for (const c of caps) {
      if (set.has(c)) { return true; }
    }
    return false;
  }

  /** True if ALL of the given caps are granted (wildcard short-circuits). */
  hasAllCaps(caps: Capability[]): boolean {
    if (this._wildcard$.value) { return true; }
    const set = this._set$.value;
    for (const c of caps) {
      if (!set.has(c)) { return false; }
    }
    return true;
  }

  /** Sync dashboard flags. */
  get dashboards(): DashboardAccess {
    return this._dashboards$.value;
  }

  /** True for super-admin / wildcard grant. */
  get isWildcard(): boolean {
    return this._wildcard$.value;
  }

  /** Manager view active (sync). */
  get isManager(): boolean {
    return this._wildcard$.value || this._set$.value.has(CAPS.SALES.LEADS.VIEW_ALL);
  }

  /** Team Lead view active (sync). */
  get isTeamLead(): boolean {
    return !this._wildcard$.value
      && (this._set$.value.has(CAPS.SALES.LEADS.VIEW_TEAM)
      || this._set$.value.has(CAPS.SALES.BUSINESS_VALIDATION.VIEW_TEAM))
      && !this._set$.value.has(CAPS.SALES.LEADS.VIEW_ALL);
  }

  /** Pure agent view active (sync). */
  get isAgent(): boolean {
    return !this._wildcard$.value
      && (this._set$.value.has(CAPS.SALES.LEADS.VIEW_OWN) || this._set$.value.has(CAPS.SALES.BUSINESS_VALIDATION.VIEW_OWN))
      && !this._set$.value.has(CAPS.SALES.LEADS.VIEW_TEAM) && !this._set$.value.has(CAPS.SALES.BUSINESS_VALIDATION.VIEW_TEAM)
      && !this._set$.value.has(CAPS.SALES.LEADS.VIEW_ALL);
  }

  /**
   * Tier hint used by sales pages that need to show L1 vs L2 vs L3 controls.
   * Returns the dashboard the user is single-scoped to, or `null` for
   * managers / multi-dashboard users (the FE picks via view_as).
   */
  get singleDashboardTier(): 1 | 2 | 3 | null {
    if (this.isManager) { return null; }
    const d = this.dashboards;
    if (d.l1 && !d.l2 && !d.l3) { return 1; }
    if (d.l2 && !d.l1 && !d.l3) { return 2; }
    if (d.l3 && !d.l1 && !d.l2) { return 3; }
    return null;
  }

  /** Capability-derived sales role flags used by sales pages and shared search. */
  get salesPermissionFlags(): SalesPermissionFlags {
    return buildSalesPermissionFlags({
      dashboards: this.dashboards,
      isAgent: this.isAgent,
      isTeamLead: this.isTeamLead,
      isManager: this.isManager
    });
  }

  // ===== Internals ==========================================================

  private hydrateFromLocalStorage(): void {
    try {
      const raw: string[] | null = this._local.customGetItem(STORAGE_KEYS.CAPABILITIES);
      const dashboardsRaw: DashboardAccess | null = this._local.customGetItem(STORAGE_KEYS.DASHBOARDS);
      const list = Array.isArray(raw) ? raw : [];
      const dashboards: DashboardAccess = dashboardsRaw
        ? {
            l1: !!dashboardsRaw.l1,
            l2: !!dashboardsRaw.l2,
            l3: !!dashboardsRaw.l3,
            l4: !!dashboardsRaw.l4
          }
        : {
            l1: list.includes(WILDCARD) || list.includes(CAPS.SALES.DASHBOARD_L1.VIEW),
            l2: list.includes(WILDCARD) || list.includes(CAPS.SALES.DASHBOARD_L2.VIEW),
            l3: list.includes(WILDCARD) || list.includes(CAPS.SALES.DASHBOARD_L3.VIEW),
            l4: list.includes(WILDCARD) || list.includes(CAPS.SALES.DASHBOARD_BUSINESS_VALIDATION.VIEW)
          };
      this.applyCapabilities(list, dashboards, /* persist */ false);
    } catch (err) {
      console.warn('[PermissionsService.hydrate] failed', err);
    }
  }

  private applyCapabilities(list: string[], dashboards: DashboardAccess, persist = true): void {
    const wildcard = list.includes(WILDCARD);
    const set = new Set<Capability>(wildcard ? [] : list);
    this._wildcard$.next(wildcard);
    this._set$.next(set);
    this._dashboards$.next(dashboards);
    if (persist) {
      try {
        this._local.customSetItem(STORAGE_KEYS.CAPABILITIES, list);
        this._local.customSetItem(STORAGE_KEYS.DASHBOARDS, dashboards);
      } catch (err) {
        console.warn('[PermissionsService.applyCapabilities] persist failed', err);
      }
    }
  }

  private evaluate(snapshot: PermissionsSnapshot, cap: Capability): boolean {
    return snapshot.wildcard || snapshot.set.has(cap);
  }

  private currentSnapshot(): PermissionsSnapshot {
    return {
      wildcard: this._wildcard$.value,
      set: this._set$.value,
      dashboards: this._dashboards$.value
    };
  }
}
